/**
 * Générateur — Attendu 949 — Connaitre et convertir des unités usuelles
 * (volume et capacité).
 * Niveau : 5ème | Domaine : Espace et géométrie / Grandeurs : volumes et unités
 *
 * Plan de cartes (version alignée sur la spécification 949 du 2026-09-21) :
 *
 *   Carte « Connaître l'échelle des volumes » — le facteur 1 000 reconstruit
 *     comme le cube de 10, et le pivot unique 1 dm³ = 1 L.
 *   Carte « Convertir des volumes » — saisie (le pointillé devient un champ,
 *     l'unité d'arrivée est affichée après) et QCM à CINQ propositions, les
 *     quatre distracteurs étant les décalages de rang des échelles concurrentes.
 *   Carte « Capacités et volumes » — même chose en passant par le pivot.
 *   Carte « Toutes les conversions » — niveau 6, mélange.
 *   Carte « Trouver l'unité de capacité » — choisir l'unité adaptée.
 *   Carte « Mise en bouteilles » — la conversion dans un problème concret.
 *
 * ── Spécification 949 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   unites_de_volume_retenues   km³, hm³, dam³, m³, dm³, cm³, mm³ (les sept rangs)
 *   unites_de_capacite_retenues hL, daL, L, dL, cL, mL (le kilolitre est écarté)
 *   nombre_de_rangs_franchis    1 ou 2 (facteur 1 000 ou 1 000 000)
 *   facteur_entre_deux_rangs    1 000, reconstruit comme 10 × 10 × 10
 *   equivalence_pivot           1 dm³ = 1 L, pivot UNIQUE
 *   nombre_a_convertir          0,02 à 999 (voir « non tranché » pour la borne 9 900)
 *   nombre_de_chiffres_significatifs 1 à 3, le dernier jamais nul
 *   nombre_de_decimales_du_nombre_a_convertir 0 à 2
 *   nombre_de_lignes_du_tableau 1 (une question par tableau)
 *
 * Exclusions appliquées : aucune conversion de longueur, d'aire, de masse, de
 * prix ni d'unité informatique ; jamais un nombre dont le dernier chiffre est
 * nul ; jamais plus de deux rangs franchis ; aucune écriture scientifique (tout
 * est formaté depuis une mantisse ENTIÈRE et un exposant) ; le tableau de
 * conversion est toujours VIDE dans l'énoncé ; le passage volume ↔ capacité est
 * toujours annoncé par le pivot.
 *
 * Erreurs visées → mots-clés :
 *   Appliquer l'échelle des longueurs (×10, ×100 par rang) → echelle_des_longueurs
 *   Compter les rangs sur la mauvaise ligne (facteur 10 000) → echelle_melangee
 *   Retenir 1 000 sans voir le cube de 10               → facteur_non_reconstruit
 *   Apprendre huit équivalences au lieu du pivot        → equivalences_separees
 *   Croire qu'une conversion change forcément le nombre → conversion_change_le_nombre
 *   Convertir en ajoutant des zéros, échouer sur un décimal → ajout_de_zeros
 *   Bon nombre de rangs, mauvais sens                   → sens_inverse
 *
 * Graduation → cartes :
 *   niveau 1 (volume → volume, multiplication)  volumes_* (sens tiré)
 *   niveau 2 (volume → volume, division)        volumes_*
 *   niveau 3 (les deux sens mélangés)           volumes_*
 *   niveau 4 (volume → litre, par le pivot)     capacites_*
 *   niveau 5 (litre → volume)                   capacites_*
 *   niveau 6 (mélange)                          melange_qcm_unique, bouteilles_*
 *   réglage : nombres entiers ou décimaux
 *   réglage : tableau de conversion dans l'énoncé ou absent
 *   réglage : saisie numérique ou cinq propositions
 *
 * ARITHMÉTIQUE EXACTE (vigilance 1 et 2) : un nombre est un couple (mantisse
 * ENTIÈRE, exposant), une conversion ajoute un exposant, et l'écriture décimale
 * est produite par déplacement de la virgule. Aucune multiplication flottante,
 * donc jamais de 0,30000000000000004 ni de notation scientifique.
 */

import {
    randInt, randomChoice, shuffle, makeSaisie, makeTexteTrou, makeQcm, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 949;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONN = 'Connaître l’échelle des volumes';
const L_VOL = 'Convertir des volumes';
const L_CAP = 'Capacités et volumes';
const L_MEL = 'Toutes les conversions';
const L_UNITE = 'Trouver l’unité de capacité';
const L_BOUT = 'Mise en bouteilles';

// ─── Échelle unifiée : exposant décimal par rapport au dm³ (= L) ─────────────

const VOL = ['km³', 'hm³', 'dam³', 'm³', 'dm³', 'cm³', 'mm³'];
const CAP = ['hL', 'daL', 'L', 'dL', 'cL', 'mL'];
// Exposant décimal de chaque unité par rapport au dm³ (= L) : 1 km = 1 000 m
// donne 1 km³ = 10^9 m³ = 10^12 dm³, et ainsi de suite de mille en mille.
const EXP = {
    'km³': 12, 'hm³': 9, 'dam³': 6, 'm³': 3, 'dm³': 0, 'cm³': -3, 'mm³': -6,
    hL: 2, daL: 1, L: 0, dL: -1, cL: -2, mL: -3,
};

/** Écriture décimale ordinaire de m × 10^e (m entier ≥ 0), virgule française. */
const ecrire = (m, e) => {
    if (e >= 0) return String(m) + '0'.repeat(e);
    const d = -e;
    const t = String(m).padStart(d + 1, '0');
    const ent = t.slice(0, t.length - d);
    const frac = t.slice(t.length - d).replace(/0+$/, '');
    return frac ? `${ent},${frac}` : ent;
};

// ─── Tirage du nombre à convertir ────────────────────────────────────────────

/**
 * Un nombre à 1 à 3 chiffres significatifs dont le dernier n'est JAMAIS nul
 * (exclusion 2), avec 0 à 2 décimales. Renvoyé sous forme exacte (m, e) :
 * la valeur vaut m × 10^(-e10).
 */
const tirerNombre = (decimal) => {
    for (let essai = 0; essai < 60; essai++) {
        const chiffres = randInt(1, 3);
        const m = randInt(10 ** (chiffres - 1), 10 ** chiffres - 1);
        if (m % 10 === 0) continue;                       // exclusion 2
        const dec = decimal ? randInt(1, 2) : 0;
        if (dec === 2 && m < 2) continue;                 // borne basse 0,02
        return { m, dec };
    }
    return { m: 23, dec: decimal ? 1 : 0 };
};

const valeurTexte = (n) => ecrire(n.m, -n.dec);

// ─── Tableau de conversion (le seul dispositif graphique, objets 2 et 3) ─────

/**
 * Grille des rangs de km³ à mm³, trois colonnes par rang (chaque rang vaut
 * mille), et la ligne des capacités alignée de sorte que le litre se place sous
 * les unités de dm³. Toujours VIDE (exclusion 5).
 */
const svgTableau = (avecCapacites) => {
    // Largeur : 21 colonnes (7 rangs de mille, 3 chiffres chacun). À 23 px la
    // grille faisait 485 px et débordait de la carte de question, qui en fait 300
    // à 440 (retour prof 2026-09-26) ; réduite par le conteneur, elle serait
    // tombée à 6 px de texte. À 14 px elle tient dans 296 px, donc s'affiche à sa
    // taille réelle sous la borne `small`, et les libellés gardent leur corps.
    const col = 14, hdr = 20, lig = 24;
    // Les six libelles de capacite se suivent d'une colonne a l'autre : a 9 px,
    // « daL » (3 caracteres) debordait sur « hL » et sur « L ». A 8 px il reste
    // 1,6 px entre deux libelles voisins. Les rangs de volume, eux, disposent de
    // trois colonnes et gardent leur corps 11.
    const FS_VOL = 11, FS_CAP = 8;
    const nCol = VOL.length * 3;
    const W = nCol * col + 2, H = hdr + (avecCapacites ? hdr : 0) + lig + 2;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`];
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);
    // en-tête des volumes : une cellule de trois colonnes par rang
    VOL.forEach((u, i) => {
        const x = 1 + i * 3 * col;
        out.push(`<rect x="${x}" y="1" width="${3 * col}" height="${hdr}" fill="#e0e7ff" stroke="#334155" stroke-width="1"/>`);
        out.push(`<text x="${x + 1.5 * col}" y="${1 + hdr - 6}" text-anchor="middle" font-size="${FS_VOL}" font-weight="700" fill="#1e3a8a">${u}</text>`);
    });
    // en-tête des capacités : hL daL L sous dm³, dL cL mL sous cm³
    const CAP_COL = { 12: 'hL', 13: 'daL', 14: 'L', 15: 'dL', 16: 'cL', 17: 'mL' };
    if (avecCapacites) {
        for (let c = 0; c < nCol; c++) {
            const x = 1 + c * col;
            out.push(`<rect x="${x}" y="${1 + hdr}" width="${col}" height="${hdr}" fill="#ecfdf5" stroke="#334155" stroke-width="1"/>`);
            if (CAP_COL[c]) out.push(`<text x="${x + col / 2}" y="${1 + 2 * hdr - 6}" text-anchor="middle" font-size="${FS_CAP}" font-weight="700" fill="#065f46">${CAP_COL[c]}</text>`);
        }
    }
    // ligne de saisie, entièrement vide
    const y0 = 1 + hdr + (avecCapacites ? hdr : 0);
    for (let c = 0; c < nCol; c++) {
        out.push(`<rect x="${1 + c * col}" y="${y0}" width="${col}" height="${lig}" fill="white" stroke="#334155" stroke-width="1"/>`);
    }
    out.push('</svg>');
    return out.join('');
};

const elementTableau = (avecCapacites) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: {
            imageUrl: `data:image/svg+xml,${encodeURIComponent(svgTableau(avecCapacites))}`,
            maxWidth: 'small',
            alt: avecCapacites ? 'Tableau de conversion des volumes et des capacités, vide' : 'Tableau de conversion des volumes, vide',
        },
    }],
});

// ─── Conversions ─────────────────────────────────────────────────────────────

/** Couples (départ, arrivée) franchissant 1 ou 2 rangs de volume (exclusion 3). */
const couplesVolume = () => {
    const out = [];
    VOL.forEach((a) => VOL.forEach((b) => {
        const k = EXP[a] - EXP[b];
        if (k === 3 || k === 6 || k === -3 || k === -6) out.push([a, b, k]);
    }));
    return out;
};
/**
 * Couples volume ↔ LITRE, dans les deux sens : `graduation_difficulte` ne nomme
 * que le litre aux niveaux 4 et 5. Le facteur nul (dm³ → L) est gardé, c'est une
 * question utile (vigilance 8). Les autres unités de capacité vivent dans le
 * tableau et dans la carte « trouver l'unité ».
 */
const couplesCapacite = () => {
    const out = [];
    VOL.forEach((a) => {
        const k = EXP[a] - EXP.L;
        if (Math.abs(k) <= 6) { out.push([a, 'L', k]); out.push(['L', a, -k]); }
    });
    return out;
};
const COUPLES_VOL = couplesVolume();
const COUPLES_CAP = couplesCapacite();

const PIVOT = 'Rappel : <b>1 dm³ = 1 L</b>.';

// ─── Distracteurs des QCM (type_reponse : cinq propositions) ─────────────────

/**
 * Quatre distracteurs, construits un par un — jamais six pour n'en garder que
 * quatre (vigilance 4). Ce sont les décalages de rang des échelles concurrentes :
 * dix ou cent au lieu de mille par rang, dix mille (échelles mélangées), et le
 * bon nombre de rangs dans le mauvais sens.
 */
const quatreDistracteurs = (n, k) => {
    const rangs = k / 3;                        // ±1 ou ±2 rangs, 0 pour dm³ → L
    if (rangs === 0) {
        // Le facteur vaut 1 (vigilance 8) : les distracteurs disent qu'une
        // conversion changerait forcément le nombre.
        return [
            { code: 'conversion_change_le_nombre', e: 1 },
            { code: 'conversion_change_le_nombre', e: -1 },
            { code: 'echelle_des_longueurs', e: 3 },
            { code: 'echelle_des_longueurs', e: -3 },
        ];
    }
    return [
        { code: 'echelle_des_longueurs', e: rangs },          // 10 par rang
        { code: 'echelle_des_longueurs', e: 2 * rangs },      // 100 par rang
        { code: 'echelle_melangee', e: 4 * rangs },           // 10 000 par rang
        { code: 'sens_inverse', e: -3 * rangs },              // bon facteur, mauvais sens
    ];
};

/** Donne sa valeur à chaque distracteur : mantisse inchangée, exposant décalé. */
const valuerDistracteurs = (liste, n) => liste.map((d) => ({ code: d.code, valeur: ecrire(n.m, d.e - n.dec) }));

const genConversionQcm = (label, couples, avecPivot, niveauDe) => () => {
    let decimal, from, to, k, n, correct, distracteurs;
    for (let essai = 0; essai < 60; essai++) {
        decimal = randomChoice([false, true]);
        [from, to, k] = randomChoice(couples);
        n = tirerNombre(decimal);
        correct = ecrire(n.m, k - n.dec);
        distracteurs = valuerDistracteurs(quatreDistracteurs(n, k), n);
        // vigilance 3 : aucune proposition ne coïncide avec une autre
        if (new Set([correct, ...distracteurs.map((d) => d.valeur)]).size === 5) break;
    }
    const texte = `<p>${avecPivot ? `${PIVOT} ` : ''}Convertis : `
        + `<b>${valeurTexte(n)} ${from} = … ${to}</b></p>`
        + `<p><i>Choisis le nombre qui remplace les pointillés.</i></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct, texte, opts(label));
    payload._meta.params = {
        niveau: niveauDe(from, to, k), from, to, rangs: k / 3, decimal,
        mantisse: n.m, decimales: n.dec, distracteurs,
        erreursVisees: decimal ? ['ajout_de_zeros'] : [],
    };
    return payload;
};

const genConversionSaisie = (label, couples, avecPivot, niveauDe) => () => {
    const decimal = randomChoice([false, true]);
    const [from, to, k] = randomChoice(couples);
    const n = tirerNombre(decimal);
    const correct = ecrire(n.m, k - n.dec);
    const tableau = randomChoice([true, false]);
    const texte = `<p>${avecPivot ? `${PIVOT} ` : ''}Convertis : `
        + `<b>${valeurTexte(n)} ${from} = … ${to}</b></p>`
        + `<p><i>Écris seulement le nombre : l'unité est déjà affichée.</i></p>`;
    const payload = makeSaisie(correct, texte, {
        ...opts(label), suffixe: to,
        elements_additionnels: tableau ? elementTableau(avecPivot) : null,
    });
    payload._meta.params = {
        niveau: niveauDe(from, to, k), from, to, rangs: k / 3, decimal,
        mantisse: n.m, decimales: n.dec, tableau,
        erreursVisees: [
            ...(decimal ? ['ajout_de_zeros'] : []),
            ...(k === 0 ? ['conversion_change_le_nombre'] : []),
            ...(avecPivot ? ['equivalences_separees'] : []),
        ],
    };
    return payload;
};

const niveauVolume = (from, to, k) => (k > 0 ? 'n1' : 'n2');
const niveauCapacite = (from) => (VOL.includes(from) ? 'n4' : 'n5');
const niveauMelange = () => 'n6';

// ═══════════════════════════════════════════════════════════
// Carte — Connaître l'échelle des volumes
// ═══════════════════════════════════════════════════════════

// Unité de longueur de chaque rang : le facteur 1 000 se reconstruit avec elle.
const LONGUEUR = { 'km³': 'km', 'hm³': 'hm', 'dam³': 'dam', 'm³': 'm', 'dm³': 'dm', 'cm³': 'cm', 'mm³': 'mm' };

/** Quatre questions de connaissance, paramétrées par le rang et par le nombre. */
const questionsConnaitre = () => {
    const i = randInt(0, VOL.length - 2);
    const grand = VOL[i], petit = VOL[i + 1];
    const v = randInt(1, 9);
    return [
        {
            question: `Pourquoi 1 ${grand} vaut-il 1 000 ${petit} ?`,
            correct: `Parce que 1 ${LONGUEUR[grand]} = 10 ${LONGUEUR[petit]}, et que 10 × 10 × 10 = 1 000.`,
            distracteurs: [
                // Ce distracteur disait « le tableau des volumes avance de 1 000 en 1 000 :
                // c'est une regle a retenir ». C'etait VRAI : l'eleve qui le cochait etait
                // compte faux sans rien avoir affirme de faux (retour prof 2026-09-26).
                // La formulation nie desormais la reconstruction du facteur, ce qui EST
                // faux (1 000 = 10 x 10 x 10) et reste l'erreur visee.
                { code: 'facteur_non_reconstruit', valeur: 'Parce que 1 000 est le facteur du tableau des volumes : c’est un nombre à retenir, il ne se déduit d’aucun calcul.' },
                { code: 'echelle_des_longueurs', valeur: `Parce que 1 ${LONGUEUR[grand]} = 100 ${LONGUEUR[petit]}.` },
                { code: 'ajout_de_zeros', valeur: 'Parce qu’il faut ajouter trois zéros.' },
            ],
        },
        {
            question: 'Pour convertir un volume, on passe d’un rang au rang suivant en multipliant par…',
            correct: '1 000',
            distracteurs: [
                { code: 'echelle_des_longueurs', valeur: '10' },
                { code: 'echelle_des_longueurs', valeur: '100' },
                { code: 'echelle_melangee', valeur: '10 000' },
            ],
        },
        {
            question: 'Quelle égalité suffit à relier toutes les unités de volume et de capacité ?',
            correct: '1 dm³ = 1 L',
            distracteurs: [
                { code: 'equivalences_separees', valeur: '1 m³ = 1 L' },
                { code: 'equivalences_separees', valeur: '1 cm³ = 1 L' },
                { code: 'equivalences_separees', valeur: 'Aucune : il faut retenir une égalité différente pour chaque unité.' },
            ],
        },
        {
            question: `Un récipient a un volume de ${v} dm³. Quelle est sa capacité ?`,
            correct: `${v} L`,
            distracteurs: [
                { code: 'conversion_change_le_nombre', valeur: `${ecrire(v, 1)} L` },
                { code: 'conversion_change_le_nombre', valeur: `${ecrire(v, -1)} L` },
                { code: 'equivalences_separees', valeur: `${ecrire(v, 3)} L` },
            ],
        },
    ];
};

const genConnaitre = () => {
    const q = randomChoice(questionsConnaitre());
    const payload = makeQcm([q.correct, ...q.distracteurs.map((d) => d.valeur)], q.correct,
        `<p><b>${q.question}</b></p>`, {
            ...opts(L_CONN),
            elements_additionnels: randomChoice([true, false]) ? elementTableau(true) : null,
        });
    payload._meta.params = {
        niveau: 'n6', from: 'dm³', to: 'L', rangs: 0, decimal: false,
        distracteurs: q.distracteurs, erreursVisees: ['facteur_non_reconstruit'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Trouver l'unité de capacité
// ═══════════════════════════════════════════════════════════

const genUnite = () => {
    const from = randomChoice(VOL.filter((u) => Math.abs(EXP[u]) <= 3));
    // Au plus un rang de volume d'écart : « 95 400 000 cm³ = 954 hL » serait juste
    // mais illisible, et la spec ne borne pas cet écart (voir le rapport).
    const to = randomChoice(CAP.filter((u) => Math.abs(EXP[u] - EXP[from]) <= 3));
    const n = tirerNombre(false);
    const vDepart = ecrire(n.m, EXP[to] - EXP[from]);
    const phrase = `${vDepart} ${from} = ${valeurTexte(n)} {{${to}}}.`;
    const leurres = shuffle(CAP.filter((u) => u !== to)).slice(0, 4);
    const payload = makeTexteTrou(phrase,
        `<p>Complète avec l'<b>unité de capacité</b> qui convient. ${PIVOT}</p>`, {
            ...opts(L_UNITE), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, leurres),
        });
    payload._meta.params = {
        niveau: 'n4', from, to, rangs: null, decimal: false,
        mantisse: n.m, decimales: n.dec,
        distracteurs: leurres.map((u) => ({ code: 'echelle_melangee', valeur: u })),
        erreursVisees: ['equivalences_separees'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Mise en bouteilles (la conversion dans un problème)
// ═══════════════════════════════════════════════════════════

// Contenances en CENTILITRES, pour que tout le calcul reste entier.
const CONTENANCES = [50, 75, 100, 125, 150, 200, 250, 500];

const genBouteillesQcm = () => {
    const m3 = randInt(1, 3);
    const litres = m3 * 1000;
    const cl = randomChoice(CONTENANCES);
    const nb = randInt(300, 900);
    const dispoCl = nb * cl;
    const suffisant = dispoCl >= litres * 100;
    const dispo = ecrire(dispoCl, -2);
    const taille = ecrire(cl, -2);
    const correct = suffisant
        ? `Oui : ${nb} × ${taille} = ${dispo} L, et ${dispo} L ≥ ${litres} L.`
        : `Non : ${nb} × ${taille} = ${dispo} L, et ${dispo} L < ${litres} L.`;
    const distracteurs = [
        {
            code: 'sens_inverse',
            valeur: suffisant
                ? `Non : ${nb} × ${taille} = ${dispo} L, et ${dispo} L < ${litres} L.`
                : `Oui : ${nb} × ${taille} = ${dispo} L, et ${dispo} L ≥ ${litres} L.`,
        },
        { code: 'echelle_des_longueurs', valeur: `Oui : ${m3} m³ font ${m3 * 10} L, il y a bien assez de bouteilles.` },
        { code: 'conversion_change_le_nombre', valeur: 'On ne peut pas comparer : les m³ et les litres ne se comparent pas.' },
    ];
    const texte = `<p>On veut mettre en bouteilles <b>${m3} m³</b> d'eau. `
        + `On dispose de <b>${nb} bouteilles de ${taille} L</b>. ${PIVOT}</p>`
        + '<p><b>Est-ce suffisant ?</b></p>';
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct, texte, opts(L_BOUT));
    payload._meta.params = {
        niveau: 'n6', from: 'm³', to: 'L', rangs: 1, decimal: cl % 100 !== 0,
        distracteurs, erreursVisees: ['equivalences_separees'],
    };
    return payload;
};

const genBouteillesSaisie = () => {
    const m3 = randInt(1, 5);
    const cl = randomChoice(CONTENANCES);
    const nb = Math.ceil((m3 * 100000) / cl);          // tout en centilitres : division ENTIÈRE
    const texte = `<p>On veut mettre <b>${m3} m³</b> d'eau en bouteilles de `
        + `<b>${ecrire(cl, -2)} L</b>. ${PIVOT}</p>`
        + '<p><b>Combien de bouteilles faut-il au minimum ?</b></p>';
    const payload = makeSaisie(String(nb), texte, { ...opts(L_BOUT), suffixe: 'bouteilles' });
    payload._meta.params = {
        niveau: 'n6', from: 'm³', to: 'L', rangs: 1, decimal: cl % 100 !== 0,
        erreursVisees: ['equivalences_separees', 'echelle_des_longueurs'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const COUPLES_TOUS = [...COUPLES_VOL, ...COUPLES_CAP];

const GENERATORS = {
    connaitre_qcm_unique: genConnaitre,
    volumes_saisie: genConversionSaisie(L_VOL, COUPLES_VOL, false, niveauVolume),
    volumes_qcm_unique: genConversionQcm(L_VOL, COUPLES_VOL, false, niveauVolume),
    capacites_saisie: genConversionSaisie(L_CAP, COUPLES_CAP, true, niveauCapacite),
    capacites_qcm_unique: genConversionQcm(L_CAP, COUPLES_CAP, true, niveauCapacite),
    melange_qcm_unique: genConversionQcm(L_MEL, COUPLES_TOUS, true, niveauMelange),
    unite_texte_trou_drag: genUnite,
    bouteilles_qcm_unique: genBouteillesQcm,
    bouteilles_saisie: genBouteillesSaisie,
};

const FORMAT_LABELS = {
    saisie: 'Saisie', texte_trou_drag: 'Étiquettes', qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'connaitre', label: L_CONN, formatsList: buildFormats('connaitre') },
    { id: 'volumes', label: L_VOL, formatsList: buildFormats('volumes') },
    { id: 'capacites', label: L_CAP, formatsList: buildFormats('capacites') },
    { id: 'melange', label: L_MEL, formatsList: buildFormats('melange') },
    { id: 'unite', label: L_UNITE, formatsList: buildFormats('unite') },
    { id: 'bouteilles', label: L_BOUT, formatsList: buildFormats('bouteilles') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { ecrire, EXP, VOL, CAP, COUPLES_VOL, COUPLES_CAP, tirerNombre };
